import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { tenantApi } from '../api/tenantApi';
import { authEnabled } from '../auth/config';

const STORAGE_KEY = 'activeTenantCode';

/**
 * Safe localStorage wrapper — Safari private mode, quota errors, and
 * `typeof window === 'undefined'` (SSR / tests) all need to be tolerated.
 * Silently no-op on failure; the app continues without persistence.
 */
const safeStorage = {
  get: (key: string): string | null => {
    try {
      return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  },
  set: (key: string, value: string): void => {
    try {
      if (typeof window !== 'undefined') window.localStorage.setItem(key, value);
    } catch {
      /* ignore */
    }
  },
  remove: (key: string): void => {
    try {
      if (typeof window !== 'undefined') window.localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

interface TenantContextValue {
  activeTenantCode: string;
  setActiveTenantCode: (code: string) => void;
  /** True while we validate the persisted tenant code against the available tenant list on mount. */
  validating: boolean;
}

const TenantContext = createContext<TenantContextValue | null>(null);

/**
 * Inner provider body — runs the persisted-tenant-code validation effect.
 * Accepts `authReady` so it can skip the API call until Auth0 has finished
 * bootstrapping and a bearer token is attachable (otherwise the call
 * 401s → axiosClient redirects to `/` → reload loop).
 */
const TenantProviderInner: React.FC<{
  children: React.ReactNode;
  authReady: boolean;
}> = ({ children, authReady }) => {
  const queryClient = useQueryClient();
  const [activeTenantCode, setCode] = useState(() => safeStorage.get(STORAGE_KEY) || '');
  const [validating, setValidating] = useState<boolean>(() => {
    // Pre-login: nothing to validate yet, and pages reading `validating`
    // shouldn't spin forever waiting for auth that may never complete here.
    if (!authReady) return false;
    return !!safeStorage.get(STORAGE_KEY);
  });
  const prevAuthReady = useRef(authReady);

  // Fetch the tenant list via React Query so this provider shares the cache
  // with the tenant picker and anything else that reads the same endpoint.
  const { data: tenants } = useQuery<Array<{ tenant_code: string }>>({
    queryKey: ['tenants'],
    queryFn: () => tenantApi.getAll(),
    enabled: authReady,
  });

  // Validate the persisted tenant code once the tenant list arrives.
  // If the stored code no longer appears in the user's visible tenants
  // (e.g. tenant was deleted or access removed), clear it so the user is
  // forced to re-select. Preserve selection on network failure so transient
  // errors don't wipe state — we simply don't mutate here.
  useEffect(() => {
    if (!authReady) {
      setValidating(false);
      return;
    }
    if (!tenants) return; // still loading or errored — stay in validating
    const persisted = safeStorage.get(STORAGE_KEY) || '';
    if (!persisted) {
      setValidating(false);
      return;
    }
    const exists = tenants.some((t) => t?.tenant_code === persisted);
    if (!exists) {
      safeStorage.remove(STORAGE_KEY);
      setCode('');
    }
    setValidating(false);
  }, [authReady, tenants]);

  // Logout clearing: when Auth0 transitions from authenticated to not,
  // wipe the persisted tenant selection so shared machines don't leak
  // the prior user's tenant to the next user.
  useEffect(() => {
    const wasReady = prevAuthReady.current;
    prevAuthReady.current = authReady;
    if (wasReady && !authReady) {
      safeStorage.remove(STORAGE_KEY);
      setCode('');
      // Also purge any cached tenant-scoped data.
      queryClient.clear();
    }
  }, [authReady, queryClient]);

  // Cross-tab sync: if another tab updates or clears the persisted code
  // (e.g. logout in another tab), mirror the change here. Don't invalidate
  // queries — each tab's data is keyed by its own URL's tenantCode, so
  // there's nothing to refetch just because localStorage changed.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY) return;
      setCode(e.newValue || '');
    };
    if (typeof window === 'undefined') return;
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Keep a ref so the setter callback can compare against the latest code
  // without being re-created on every render (which would churn any
  // downstream useEffect dep arrays that capture it).
  const activeCodeRef = useRef(activeTenantCode);
  activeCodeRef.current = activeTenantCode;

  const setActiveTenantCode = useCallback(
    (code: string) => {
      // No-op if the code didn't change. Matters because Layout syncs the URL
      // tenantCode to context on every mount; without this guard we'd clear
      // the query cache on every page-to-page nav within the same tenant.
      if (activeCodeRef.current === code) return;
      if (code) {
        safeStorage.set(STORAGE_KEY, code);
      } else {
        safeStorage.remove(STORAGE_KEY);
      }
      setCode(code);
      // Drop stale tenant-scoped cached queries when switching tenants.
      // Three classes of queries must be preserved to avoid spurious spinners:
      //   ['me']     — user-level; clearing it spikes useCurrentUser.isLoading
      //   ['tenants']— user-level tenant list; unrelated to the active tenant
      //   any key containing `code` — these were already fetched by the newly-
      //                               mounted components for the new tenant;
      //                               removing them causes an immediate double-
      //                               fetch and a visible stuck spinner.
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key) || key.length === 0) return true;
          if (key[0] === 'me' || key[0] === 'tenants') return false;
          if (key.includes(code)) return false;
          return true;
        },
      });
    },
    [queryClient],
  );

  const value = useMemo(
    () => ({ activeTenantCode, setActiveTenantCode, validating }),
    [activeTenantCode, setActiveTenantCode, validating],
  );

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/** Auth-gated wrapper: only calls useAuth0 when Auth0 is actually enabled. */
const TenantProviderWithAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const authReady = !isLoading && isAuthenticated;
  return <TenantProviderInner authReady={authReady}>{children}</TenantProviderInner>;
};

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // When Auth0 isn't configured (local dev without client ID), skip auth
  // gating entirely — the backend isn't expecting a bearer token anyway.
  if (!authEnabled) {
    return <TenantProviderInner authReady>{children}</TenantProviderInner>;
  }
  return <TenantProviderWithAuth>{children}</TenantProviderWithAuth>;
};

/**
 * Hook to read/write the active tenant selection.
 * Throws if used outside a TenantProvider to surface misuse early.
 */
export const useTenant = (): TenantContextValue => {
  const ctx = useContext(TenantContext);
  if (ctx === null) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return ctx;
};

/**
 * Non-throwing variant of useTenant for LEAF UI that may legitimately render
 * outside a TenantProvider — e.g. a shared write-affordance (WriteButton) used
 * on the tenant picker or in a unit test that doesn't mount the provider.
 * Returns null when there is no provider; callers treat that as "no active
 * tenant" (env-level behavior). Real tenant-scoped code keeps using useTenant()
 * so a genuinely-missing provider still surfaces loudly there.
 */
export const useTenantOptional = (): TenantContextValue | null =>
  useContext(TenantContext);
